import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import UserBenefitsBar from './UserBenefitsBar'
import { Button, buttonVariants } from '@/components/ui/button'
import { uniqBy } from 'lodash'
import { useTranslation } from 'react-i18next'
import { revokeSubscriptionBenefits } from 'app/frontend/base/api/user'
import { displayErrorToast } from 'app/frontend/base/helpers'
import { useQueryClient } from '@tanstack/react-query'
import Card from 'app/frontend/base/components/Card'
import { useCustomDomain } from 'app/frontend/base/hooks/useCustomDomain'

interface Props {
  subscription: wowaudit.Subscription
  color: string
}

const SubscriptionInfo = ({ subscription, color }: Props) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [revoking, setRevoking] = useState<number>()
  const { customDomain } = useCustomDomain()

  const pointsSpentByEntity = useMemo(() => {
    const aggregate: { [recipientId: number]: number } = {}

    for (const benefit of subscription.premiumBenefits) {
      if (!aggregate[benefit.recipientId]) {
        aggregate[benefit.recipientId] = 0
      }

      aggregate[benefit.recipientId] += benefit.points
    }

    return aggregate
  }, [subscription])

  const beneficiaries = uniqBy(subscription.premiumBenefits, 'recipientId')

  return (
    <div>
      <h5>
        You are subscribed to the{' '}
        <strong className={`${color}`}>{subscription.tierName}</strong> plan
      </h5>

      <UserBenefitsBar
        points={subscription.points}
        pointSpentPerEntity={Object.values(pointsSpentByEntity)}
        label='points'
      />

      <Card className='py-2 px-3'>
        <h5>Active benefits</h5>
        <div className='text-sm'>
          {beneficiaries.length === 0 && (
            <p className='italic opacity-70'>
              {t('profile.premium.nothing-granted')}
            </p>
          )}
          {beneficiaries.map((beneficiary) => {
            const pointsSpent = pointsSpentByEntity[beneficiary.recipientId]

            return (
              <Card
                key={beneficiary.recipientId}
                className=' mb-2 flex justify-between'
              >
                <span>{beneficiary.recipientName}</span>
                <div className='flex items-center gap-4'>
                  <span>
                    {pointsSpent} point{pointsSpent === 1 ? '' : 's'}
                  </span>
                  <div className='flex gap-2'>
                    {customDomain ? (
                      <a
                        href={`${beneficiary.recipientFullUrl}/premium`}
                        className={buttonVariants({
                          variant: 'outline',
                          size: 'xs',
                        })}
                      >
                        {t('shared.view')}
                      </a>
                    ) : (
                      <Link to={`${beneficiary.recipientPath}/premium`}>
                        <Button variant='outline' size='xs'>
                          {t('shared.view')}
                        </Button>
                      </Link>
                    )}

                    <Button
                      variant='outline'
                      size='xs'
                      loading={revoking === beneficiary.recipientId}
                      onClick={() => {
                        setRevoking(beneficiary.recipientId)
                        revokeSubscriptionBenefits(
                          beneficiary.recipientId,
                          beneficiary.recipientType,
                        )
                          .then(async () => {
                            await queryClient.invalidateQueries({
                              queryKey: ['pageInfo'],
                            })
                            setRevoking(undefined)
                          })
                          .catch((err) => {
                            displayErrorToast(err, t)
                            setRevoking(undefined)
                          })
                      }}
                    >
                      {t('shared.revoke')}
                    </Button>
                  </div>
                </div>
              </Card>
            )
          })}
        </div>
      </Card>
    </div>
  )
}

export default SubscriptionInfo
